/* ==========================================================================
   SCURA — Theme JS
   Vanilla JS only, no framework. Organized into small self-contained
   modules that each check for their markup before running.
   ========================================================================== */

document.addEventListener('DOMContentLoaded', function () {
  initPageLoader();
  initHeroStar();
  initHeroScrollFade();
  initScrollReveal();
  initManifesto();
  initMobileNav();
  initCartDrawer();
  initQuantitySteppers();
  initProductGallery();
  initVariantSelectors();
  initAccordions();
  initStickyAddToCart();
  initProductRecommendations();
  initCollectionNav();
  initFlipCards();
  initFitFinder();
  initQualityStats();
  initCustomCursor();
  initMagneticButtons();
  initPageTransitions();
  initHoverSounds();
});

/* ---------------------------------------------------------------------
   Hero star intro: jagged draw-in -> morph to one clean star ->
   the "scura" word turns into the five stars.
   Also wires up the tiny replay / disable controls in the corner.
   --------------------------------------------------------------------- */
function initHeroStar() {
  var STORAGE_KEY = 'scuraHeroAnimDisabled';
  var STARS_HIDDEN_KEY = 'scuraStarsHidden';
  var heroSection = document.querySelector('.hero');
  var heroStar = document.getElementById('hero-star');
  var fiveStars = document.getElementById('five-stars');
  var heroWord = document.getElementById('hero-word');
  var replayBtn = document.querySelector('[data-hero-replay]');
  var disableBtn = document.querySelector('[data-hero-disable]');
  var hideStarsBtn = document.querySelector('[data-hero-hide-stars]');
  if (!heroStar || !fiveStars) return;

  function isStarsHidden() {
    try { return window.localStorage.getItem(STARS_HIDDEN_KEY) === '1'; }
    catch (e) { return false; }
  }
  function setStarsHidden(value) {
    try {
      if (value) window.localStorage.setItem(STARS_HIDDEN_KEY, '1');
      else window.localStorage.removeItem(STARS_HIDDEN_KEY);
    } catch (e) { /* localStorage unavailable — just skip persistence */ }
  }
  // Independent of replay/disable — forces the five-star mark off no
  // matter what state the intro sequence is in.
  function applyStarsHidden() {
    fiveStars.classList.toggle('force-hidden', isStarsHidden());
  }
  function updateHideStarsButton() {
    if (!hideStarsBtn) return;
    var hidden = isStarsHidden();
    hideStarsBtn.textContent = hidden ? 'Show Stars' : 'Hide Stars';
    hideStarsBtn.classList.toggle('is-active', hidden);
    hideStarsBtn.setAttribute('aria-label', hidden ? 'Show the five-star mark' : 'Hide the five-star mark');
    hideStarsBtn.title = hidden ? 'Show stars' : 'Hide stars';
  }

  var timers = [];
  function clearTimers() {
    timers.forEach(function (id) { clearTimeout(id); });
    timers = [];
  }

  function isDisabled() {
    try { return window.localStorage.getItem(STORAGE_KEY) === '1'; }
    catch (e) { return false; }
  }
  function setDisabled(value) {
    try {
      if (value) window.localStorage.setItem(STORAGE_KEY, '1');
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* localStorage unavailable (private mode etc.) — just skip persistence */ }
  }

  // Jumps straight to the settled five-star state, no intro, no motion.
  function showFinalState() {
    clearTimers();
    heroStar.style.display = 'none';
    if (heroWord) heroWord.classList.add('word-out');
    fiveStars.classList.add('show');
    fiveStars.style.animation = 'none';
    applyStarsHidden();
  }

  // Clones the two SVGs back in fresh so their CSS + SMIL animations
  // restart from frame zero, then replays the full timed sequence.
  function playIntro() {
    clearTimers();

    // cloneNode copies the *current* class/style state too, so strip
    // whatever this run left behind before the clones go back in the DOM.
    var freshHeroStar = heroStar.cloneNode(true);
    freshHeroStar.classList.remove('fade-out', 'dim');
    freshHeroStar.style.display = '';
    heroStar.replaceWith(freshHeroStar);
    heroStar = freshHeroStar;

    var freshFiveStars = fiveStars.cloneNode(true);
    freshFiveStars.classList.remove('show');
    freshFiveStars.style.animation = '';
    fiveStars.replaceWith(freshFiveStars);
    fiveStars = freshFiveStars;

    if (heroWord) heroWord.classList.remove('word-out');

    applyStarsHidden();

    // The word starts fading in at 2s (see .hero h1's animation-delay) —
    // dim the star just ahead of that so it's a quieter background
    // presence once the word is on screen. The star's own draw-in (2.6s)
    // and shape-morph (2.6s-3.7s) are meant to still play out visibly
    // here (making it fade to 0 before this point was tried and just cut
    // the star's show short); what actually stops it from visually
    // crossing the open counter of the "a" is the opaque backdrop on
    // .hero h1 itself (see theme.css) — once the word is fully faded in,
    // its solid background fully blocks anything behind it, regardless
    // of what the star is doing.
    timers.push(setTimeout(function () {
      heroStar.classList.add('dim');
    }, 1800));

    timers.push(setTimeout(function () {
      heroStar.classList.add('fade-out');
      fiveStars.classList.add('show');
      if (heroWord) heroWord.classList.add('word-out');
    }, 3700));

    timers.push(setTimeout(function () {
      heroStar.style.display = 'none';
    }, 5100));
  }

  function updateDisableButton() {
    if (!disableBtn) return;
    var disabled = isDisabled();
    disableBtn.classList.toggle('is-disabled', disabled);
    disableBtn.innerHTML = disabled ? '&#9654;' : '&#10005;';
    disableBtn.setAttribute('aria-label', disabled ? 'Enable intro animation' : 'Disable intro animation');
    disableBtn.title = disabled ? 'Enable animation' : 'Disable animation';
  }

  // The eyebrow/word/tag/scroll-indicator fade-ins are plain CSS
  // animations gated on this class (see .hero.intro-ready in theme.css).
  // This used to also wait on whenFontsReady() to avoid painting Anton's
  // fallback font mid-animation, but that traded one race for another:
  // if the real font took longer than our wait, the animation ran on
  // schedule while the glyphs stayed invisible (font-display:block,
  // below) until whenever Anton actually finished — popping in out of
  // sync with the star's dim/fade-out timing instead of with it. Now
  // that Anton's own <link> uses display=block, the browser itself
  // guarantees no wrong-font frame is ever painted, so this can just run
  // on its original fixed timeline again.
  if (heroSection) heroSection.classList.add('intro-ready');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || isDisabled()) {
    heroStar.style.display = 'none';
    if (heroWord) heroWord.classList.add('word-out');
    fiveStars.classList.add('show');
    applyStarsHidden();
  } else {
    playIntro();
  }
  updateDisableButton();
  updateHideStarsButton();

  if (replayBtn) {
    replayBtn.addEventListener('click', function () {
      setDisabled(false);
      updateDisableButton();
      playIntro();
    });
  }

  if (disableBtn) {
    disableBtn.addEventListener('click', function () {
      if (isDisabled()) {
        setDisabled(false);
        updateDisableButton();
        playIntro();
      } else {
        setDisabled(true);
        updateDisableButton();
        showFinalState();
      }
    });
  }

  if (hideStarsBtn) {
    hideStarsBtn.addEventListener('click', function () {
      setStarsHidden(!isStarsHidden());
      applyStarsHidden();
      updateHideStarsButton();
    });
  }
}

/* ---------------------------------------------------------------------
   Fades the five-star mark out once the hero has scrolled out of view
   (it's position:fixed, so without this it keeps bleeding into every
   section below — category cards, footer, etc). Fades back in if the
   user scrolls back up into the hero.
   --------------------------------------------------------------------- */
function initHeroScrollFade() {
  var heroSection = document.querySelector('.hero');
  if (!heroSection) return;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var stars = document.getElementById('five-stars');
      if (stars) stars.classList.toggle('scrolled-out', !entry.isIntersecting);
    });
  }, { threshold: 0 });
  io.observe(heroSection);
}

/* ---------------------------------------------------------------------
   Manifesto: cascades the heading in word-by-word once it scrolls into
   view; hover adds a soft glow on top of that (handled in CSS).
   --------------------------------------------------------------------- */
function initManifesto() {
  var block = document.querySelector('[data-manifesto]');
  if (!block) return;

  splitIntoRevealWords(block.querySelector('[data-reveal-text]'), 90);

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-in-view');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.4 });
  io.observe(block);
}

/* ---------------------------------------------------------------------
   Scroll reveal for any element with the .reveal class
   --------------------------------------------------------------------- */
function initScrollReveal() {
  var els = document.querySelectorAll('.reveal');
  if (!els.length) return;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('in');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  els.forEach(function (el) { io.observe(el); });
}

/* ---------------------------------------------------------------------
   Mobile nav drawer
   --------------------------------------------------------------------- */
function initMobileNav() {
  var burger = document.querySelector('[data-mobile-nav-toggle]');
  var panel = document.querySelector('[data-mobile-nav]');
  if (!burger || !panel) return;

  burger.addEventListener('click', function () {
    var isOpen = panel.classList.toggle('is-open');
    burger.classList.toggle('is-open', isOpen);
    document.body.classList.toggle('scroll-lock', isOpen);
  });

  panel.querySelectorAll('a').forEach(function (a) {
    a.addEventListener('click', function () {
      panel.classList.remove('is-open');
      burger.classList.remove('is-open');
      document.body.classList.remove('scroll-lock');
    });
  });
}

/* ---------------------------------------------------------------------
   Cart drawer: open/close, AJAX add-to-cart, line qty + remove
   --------------------------------------------------------------------- */
function initCartDrawer() {
  var drawer = document.querySelector('[data-cart-drawer]');
  var overlay = document.querySelector('[data-cart-overlay]');
  if (!drawer || !overlay) return;

  function openDrawer() {
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.classList.add('scroll-lock');
  }
  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.classList.remove('scroll-lock');
  }

  document.querySelectorAll('[data-cart-open]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer();
    });
  });
  document.querySelectorAll('[data-cart-close]').forEach(function (btn) {
    btn.addEventListener('click', closeDrawer);
  });
  overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  // Intercept any "add to cart" form on the page and submit via AJAX
  // so the drawer can open without a full page reload.
  document.querySelectorAll('form[data-product-form]').forEach(function (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) { submitBtn.disabled = true; submitBtn.dataset.origText = submitBtn.textContent; submitBtn.textContent = 'Adding…'; }

      var formData = new FormData(form);
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      })
        .then(function (res) { return res.json(); })
        .then(function () {
          return refreshCartDrawer();
        })
        .then(function () {
          openDrawer();
        })
        .catch(function (err) { console.error('Add to cart failed:', err); })
        .finally(function () {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitBtn.dataset.origText; }
        });
    });
  });

  // Delegate qty change / remove inside the drawer
  drawer.addEventListener('click', function (e) {
    var incBtn = e.target.closest('[data-qty-increase]');
    var decBtn = e.target.closest('[data-qty-decrease]');
    var removeBtn = e.target.closest('[data-line-remove]');

    if (incBtn || decBtn) {
      var wrap = e.target.closest('[data-cart-line]');
      var input = wrap.querySelector('[data-qty-input]');
      var qty = parseInt(input.value, 10) || 1;
      qty = incBtn ? qty + 1 : Math.max(0, qty - 1);
      updateCartLine(wrap.dataset.key, qty);
    }

    if (removeBtn) {
      var lineWrap = e.target.closest('[data-cart-line]');
      updateCartLine(lineWrap.dataset.key, 0);
    }
  });

  drawer.addEventListener('change', function (e) {
    if (e.target.matches('[data-qty-input]')) {
      var wrap = e.target.closest('[data-cart-line]');
      var qty = Math.max(0, parseInt(e.target.value, 10) || 0);
      updateCartLine(wrap.dataset.key, qty);
    }
  });

  function updateCartLine(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function () { return refreshCartDrawer(); })
      .catch(function (err) { console.error('Cart update failed:', err); });
  }

  function refreshCartDrawer() {
    return fetch('/?section_id=cart-drawer')
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newDrawer = doc.querySelector('[data-cart-drawer]');
        if (newDrawer) {
          drawer.innerHTML = newDrawer.innerHTML;
        }
        updateCartCountBubble();
      });
  }

  function updateCartCountBubble() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
      });
  }
}

/* ---------------------------------------------------------------------
   Generic quantity steppers (used on cart page + drawer)
   --------------------------------------------------------------------- */
function initQuantitySteppers() {
  document.querySelectorAll('[data-qty-stepper]').forEach(function (stepper) {
    var input = stepper.querySelector('[data-qty-input]');
    var inc = stepper.querySelector('[data-qty-increase]');
    var dec = stepper.querySelector('[data-qty-decrease]');
    if (!input) return;

    if (inc) inc.addEventListener('click', function () {
      input.value = (parseInt(input.value, 10) || 1) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    if (dec) dec.addEventListener('click', function () {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

/* ---------------------------------------------------------------------
   Product page gallery: thumbnail click swaps the main image
   --------------------------------------------------------------------- */
function initProductGallery() {
  document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
    var mainImg = gallery.querySelector('[data-gallery-main] img');
    var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var newSrc = thumb.dataset.fullSrc;
        if (newSrc) mainImg.src = newSrc;
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  });
}

/* ---------------------------------------------------------------------
   Product page variant selectors: swatches update the hidden variant
   id input, price, availability and swap the gallery image if the
   chosen variant has a featured image.
   --------------------------------------------------------------------- */
function initVariantSelectors() {
  document.querySelectorAll('[data-product-form]').forEach(function (form) {
    var variantsJSON = form.querySelector('[data-variants-json]');
    if (!variantsJSON) return;
    var variants = JSON.parse(variantsJSON.textContent);

    var optionInputs = form.querySelectorAll('[data-option-index]');
    var variantIdInput = form.querySelector('[name="id"]');
    var priceEl = document.querySelector('[data-product-price]');
    var submitBtn = form.querySelector('[type="submit"]');
    var stockNote = document.querySelector('[data-stock-note]');

    function getSelectedOptions() {
      var selected = [];
      optionInputs.forEach(function (group) {
        var active = group.querySelector('.is-active');
        selected[parseInt(group.dataset.optionIndex, 10)] = active ? active.dataset.value : null;
      });
      return selected;
    }

    function findMatchingVariant() {
      var selected = getSelectedOptions();
      return variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === selected[i]; });
      });
    }

    function updateForVariant(variant) {
      if (!variant) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Unavailable'; }
        return;
      }
      if (variantIdInput) variantIdInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        submitBtn.textContent = variant.available ? (submitBtn.dataset.addText || 'Add to Cart') : 'Sold Out';
      }
      if (stockNote) {
        if (!variant.available) {
          stockNote.textContent = 'Sold out in this size';
          stockNote.classList.add('low');
        } else if (variant.inventory_quantity !== undefined && variant.inventory_quantity <= 5 && variant.inventory_quantity > 0) {
          stockNote.textContent = 'Only ' + variant.inventory_quantity + ' left';
          stockNote.classList.add('low');
        } else {
          stockNote.textContent = 'In stock, ready to ship';
          stockNote.classList.remove('low');
        }
      }
      if (variant.featured_image) {
        var mainImg = document.querySelector('[data-gallery-main] img');
        if (mainImg) mainImg.src = variant.featured_image.src;
      }
    }

    form.querySelectorAll('[data-value]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var group = btn.closest('[data-option-index]');
        group.querySelectorAll('[data-value]').forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        updateForVariant(findMatchingVariant());
      });
    });

    // set initial state
    updateForVariant(findMatchingVariant());
  });
}

function formatMoney(cents) {
  var amount = (cents / 100).toFixed(2);
  return '$' + amount;
}

/* ---------------------------------------------------------------------
   Accordion (product page details / shipping / size & fit)
   --------------------------------------------------------------------- */
function initAccordions() {
  document.querySelectorAll('[data-accordion-trigger]').forEach(function (trigger) {
    trigger.addEventListener('click', function () {
      var item = trigger.closest('[data-accordion-item]');
      item.classList.toggle('is-open');
    });
  });
}

/* ---------------------------------------------------------------------
   Sticky add-to-cart bar: mirrors the main form's button once it has
   scrolled out of view, and forwards clicks into the real form.
   --------------------------------------------------------------------- */
function initStickyAddToCart() {
  var bar = document.querySelector('[data-sticky-atc]');
  var anchor = document.querySelector('[data-atc-anchor]');
  var form = document.querySelector('[data-product-form]');
  if (!bar || !anchor || !form) return;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    });
  }, { threshold: 0 });
  io.observe(anchor);

  var stickyBtn = bar.querySelector('[data-sticky-atc-button]');
  if (stickyBtn) {
    stickyBtn.addEventListener('click', function () {
      if (form.requestSubmit) {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });
  }
}

/* ---------------------------------------------------------------------
   Product recommendations: fetched async via the Section Rendering API
   so the product page itself never waits on it.
   --------------------------------------------------------------------- */
function initProductRecommendations() {
  var el = document.querySelector('[data-product-recommendations]');
  if (!el || !el.dataset.url) return;

  fetch(el.dataset.url)
    .then(function (res) { return res.text(); })
    .then(function (html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var recSection = doc.querySelector('#shopify-section-product-recommendations');
      el.innerHTML = recSection ? recSection.innerHTML : html;
      initScrollReveal();
    })
    .catch(function (err) { console.error('Product recommendations failed to load:', err); });
}

/* ---------------------------------------------------------------------
   Splits an element's text into <span class="reveal-word"> chunks with
   a staggered transition-delay, preserving any <br> line breaks inside
   it. Used for the word-cascade reveal on scroll-into-view.
   Deliberately opacity-only (no translateY slide, no overflow:hidden
   mask) — a sliding reveal needs a mask sized to either the word or its
   container, and both were tried and both ended up clipping text under
   some combination of viewport width/card height/font metrics. A pure
   fade can't visually "cut off" a letter under any circumstance, so
   this trades a bit of motion for guaranteed-correct rendering.
   --------------------------------------------------------------------- */
function splitIntoRevealWords(el, stepMs) {
  if (!el || el.dataset.split === 'true') return;
  var step = stepMs || 60;
  var frag = document.createDocumentFragment();
  var wordIndex = 0;

  Array.prototype.forEach.call(el.childNodes, function (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      node.textContent.split(/(\s+)/).forEach(function (part) {
        if (part === '') return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
        } else {
          var span = document.createElement('span');
          span.className = 'reveal-word';
          span.textContent = part;
          span.style.transitionDelay = (wordIndex * step) + 'ms';
          wordIndex++;
          frag.appendChild(span);
        }
      });
    } else {
      frag.appendChild(node.cloneNode(true));
    }
  });

  el.textContent = '';
  el.appendChild(frag);
  el.dataset.split = 'true';
}

/* ---------------------------------------------------------------------
   Collection navigation: word-split title reveal, ambient particles
   paused off-screen, and a per-card parallax/glow driven by mousemove.
   --------------------------------------------------------------------- */
function initCollectionNav() {
  var section = document.querySelector('.collection-nav');
  var cards = document.querySelectorAll('[data-collection-card]');
  if (!section || !cards.length) return;

  cards.forEach(function (card) {
    splitIntoRevealWords(card.querySelector('[data-reveal-text]'), 70);
  });

  // Fades each card's photo in over its black background as soon as it
  // finishes decoding, instead of the image just snapping into place
  // whenever the browser happens to finish fetching it (see .is-loaded
  // in theme.css). Checks .complete first so an already-cached image
  // (repeat visits, or one that finished before this JS even ran) starts
  // its fade right away too, instead of sitting invisible waiting on a
  // 'load' event that already fired before we could listen for it.
  cards.forEach(function (card) {
    var img = card.querySelector('[data-card-img]');
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('is-loaded');
    } else {
      img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
    }
  });

  // is-in-view toggles both ways (pauses ambient particles when the card
  // is off-screen either direction) — but the title reveal must only ever
  // play once. Re-toggling it off mid-cascade (e.g. scrolling back up past
  // the threshold) would reset the words to their hidden/offset state and
  // replay the transition, which could read as the text getting clipped.
  // has-revealed is a one-time, never-removed flag just for that reveal.
  var gsapReady = typeof window.gsap !== 'undefined';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      entry.target.classList.toggle('is-in-view', entry.isIntersecting);
      if (entry.isIntersecting && !entry.target.classList.contains('has-revealed')) {
        entry.target.classList.add('has-revealed');

        // GSAP entrance flourish on top of the plain .reveal fade-up
        // (untouched, still the fallback): the media wrapper settles in
        // from a slight zoom while the index/title/desc/cta stagger up.
        // Animates .collection-card-media's own transform, never the
        // <img>'s — that's reserved for the mousemove parallax, so the
        // two can never fight over the same property. Only for cards
        // with the "Premium editorial treatment" block setting on —
        // every other card keeps the plain CSS-only fade.
        if (gsapReady && !reduceMotion && entry.target.classList.contains('is-featured')) {
          var media = entry.target.querySelector('[data-card-media]');
          var content = entry.target.querySelectorAll('.collection-card-index, .collection-card-title, .collection-card-desc, .collection-card-cta, .collection-card-badge');
          if (media) gsap.fromTo(media, { scale: 1.1 }, { scale: 1, duration: 1.3, ease: 'power3.out' });
          if (content.length) gsap.from(content, { y: 26, opacity: 0, duration: 0.9, ease: 'power2.out', stagger: 0.08, delay: 0.1 });
        }
      }
    });
  }, { threshold: 0.25 });
  // Anton's own <link> uses font-display:block (see theme.liquid), so the
  // browser itself guarantees titles are never painted in the fallback
  // font — no need to also delay observing here. That used to wait on
  // whenFontsReady() too, but a slow connection could mean a card gets
  // scrolled past before the (font-timed) observer ever started watching
  // it, permanently missing its one has-revealed opportunity.
  cards.forEach(function (card) { io.observe(card); });

  var isFinePointer = window.matchMedia('(pointer: fine)').matches;
  if (reduceMotion || !isFinePointer) return;

  cards.forEach(function (card) {
    var img = card.querySelector('[data-card-img]');
    var glow = card.querySelector('[data-card-glow]');
    // Matches whichever zoom the CSS :hover fallback uses for this card
    // (see theme.css) — 3% for the opt-in "Premium editorial treatment",
    // 8% for every other card, unchanged from before.
    var zoomScale = card.classList.contains('is-featured') ? 1.03 : 1.08;
    var raf = null;
    var pending = null;
    // Measured once per hover (not on every mousemove — the card's size
    // and position don't change mid-hover), cleared on mouseleave so the
    // next hover always re-measures instead of trusting a stale rect.
    var rect = null;
    card.addEventListener('mouseenter', function () { rect = card.getBoundingClientRect(); });

    card.addEventListener('mousemove', function (e) {
      if (!rect) rect = card.getBoundingClientRect();
      var localX = e.clientX - rect.left;
      var localY = e.clientY - rect.top;
      var relX = (localX / rect.width - 0.5) * 2;
      var relY = (localY / rect.height - 0.5) * 2;
      pending = { imgX: relX * -14, imgY: relY * -14, x: localX, y: localY };

      if (!raf) {
        raf = requestAnimationFrame(function () {
          if (img) img.style.transform = 'translate(' + pending.imgX + 'px,' + pending.imgY + 'px) scale(' + zoomScale + ')';
          if (glow) glow.style.transform = 'translate3d(' + pending.x + 'px,' + pending.y + 'px,0)';
          raf = null;
        });
      }
    });

    card.addEventListener('mouseleave', function () {
      rect = null;
      if (img) img.style.transform = '';
      if (glow) glow.style.transform = 'translate3d(-9999px,-9999px,0)';
    });
  });
}

/* ---------------------------------------------------------------------
   Custom cursor: a single shared star that follows the pointer and
   lights up over cards/buttons. Skipped entirely on touch devices.
   --------------------------------------------------------------------- */
function initCustomCursor() {
  if (!window.matchMedia('(pointer: fine)').matches) return;
  var cursor = document.querySelector('[data-cursor-star]');
  if (!cursor) return;

  var targets = 'a.collection-card, .btn-primary, .btn-ghost, .nav-icon-btn, .hero-anim-btn';
  var raf = null;
  var x = -9999, y = -9999, angle = 0, lastX = 0, lastY = 0;

  function render() {
    cursor.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) rotate(' + angle + 'deg)';
    raf = null;
  }

  document.addEventListener('mousemove', function (e) {
    var dx = e.clientX - lastX;
    var dy = e.clientY - lastY;
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
      angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    }
    lastX = e.clientX; lastY = e.clientY;
    x = e.clientX; y = e.clientY;
    if (!raf) raf = requestAnimationFrame(render);
  });

  document.addEventListener('mouseover', function (e) {
    if (e.target.closest && e.target.closest(targets)) cursor.classList.add('is-visible');
  });
  document.addEventListener('mouseout', function (e) {
    if (!e.target.closest || !e.target.closest(targets)) return;
    var to = e.relatedTarget;
    if (!to || !to.closest || !to.closest(targets)) cursor.classList.remove('is-visible');
  });
  document.addEventListener('mouseleave', function () {
    cursor.classList.remove('is-visible');
  });
}

/* ---------------------------------------------------------------------
   Magnetic buttons: nudges the button a few px toward the cursor while
   hovering, eases back on mouseleave. Skipped on touch devices.
   --------------------------------------------------------------------- */
function initMagneticButtons() {
  if (!window.matchMedia('(pointer: fine)').matches) return;
  var els = document.querySelectorAll('.btn-primary, .btn-ghost, .nav-icon-btn, .hero-anim-btn');
  if (!els.length) return;

  els.forEach(function (el) {
    el.classList.add('magnetic-el');
    var raf = null;
    var pending = null;
    // Measured once per hover, not on every mousemove — cleared on
    // mouseleave so the next hover always re-measures.
    var rect = null;
    el.addEventListener('mouseenter', function () { rect = el.getBoundingClientRect(); });

    el.addEventListener('mousemove', function (e) {
      if (!rect) rect = el.getBoundingClientRect();
      pending = [
        (e.clientX - (rect.left + rect.width / 2)) * 0.28,
        (e.clientY - (rect.top + rect.height / 2)) * 0.35
      ];
      if (!raf) {
        raf = requestAnimationFrame(function () {
          el.classList.add('is-magnetized');
          el.style.transform = 'translate(' + pending[0] + 'px,' + pending[1] + 'px)';
          raf = null;
        });
      }
    });

    el.addEventListener('mouseleave', function () {
      rect = null;
      el.classList.remove('is-magnetized');
      el.style.transform = '';
    });
  });
}

/* ---------------------------------------------------------------------
   Star-shaped page loader: brief draw-in on first load, then fades.
   Reused as a quick exit curtain for internal navigation (see
   initPageTransitions below). Has its own CSS-only safety net so it
   can never get stuck covering the page if this JS fails to run.
   --------------------------------------------------------------------- */
function initPageLoader() {
  var loader = document.querySelector('[data-page-loader]');
  if (!loader) return;

  var shownAt = Date.now();
  var minVisible = 450;

  function hide() {
    var wait = Math.max(0, minVisible - (Date.now() - shownAt));
    setTimeout(function () { loader.classList.add('is-hidden'); }, wait);
  }

  if (document.readyState === 'complete') {
    hide();
  } else {
    window.addEventListener('load', hide, { once: true });
    setTimeout(hide, 2000); // safety net if 'load' is delayed by a slow asset
  }
}

/* ---------------------------------------------------------------------
   Smooth page transitions: fades the loader curtain back in before
   following an internal link, so navigation never hard-cuts.
   --------------------------------------------------------------------- */
function initPageTransitions() {
  var loader = document.querySelector('[data-page-loader]');
  if (!loader) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest && e.target.closest('a[href]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

    var url;
    try { url = new URL(link.href, window.location.href); }
    catch (err) { return; }

    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;

    e.preventDefault();
    loader.classList.remove('is-hidden');
    loader.classList.add('is-exiting');
    setTimeout(function () { window.location.href = link.href; }, 220);
  });
}

/* ---------------------------------------------------------------------
   Hover sounds — structure only, disabled by default. Flip
   SOUND_ENABLED and drop files in assets/ (e.g. hover.mp3, click.mp3)
   to turn this on; every call site already exists via playSound().
   --------------------------------------------------------------------- */
var SOUND_ENABLED = false;
var soundCache = {};
function playSound(name) {
  if (!SOUND_ENABLED) return;
  if (!soundCache[name]) {
    try { soundCache[name] = new Audio(window.themeAssetUrls && window.themeAssetUrls[name]); }
    catch (e) { return; }
  }
  var audio = soundCache[name];
  try { audio.currentTime = 0; audio.play().catch(function () {}); } catch (e) {}
}
/* ---------------------------------------------------------------------
   Latest Drop flip cards: hover flips them on devices that actually have
   hover (handled entirely in CSS). On touch, the first tap flips the
   card instead of following the link, so a second tap on the revealed
   "View Product" button is what actually navigates.
   --------------------------------------------------------------------- */
function initFlipCards() {
  var isTouch = window.matchMedia('(hover: none)').matches;
  if (!isTouch) return;

  var cards = document.querySelectorAll('[data-flip-card]');
  cards.forEach(function (card) {
    card.addEventListener('click', function (e) {
      if (card.classList.contains('is-touch-flipped')) return; // let the tap through to the back's link
      e.preventDefault();
      cards.forEach(function (c) {
        if (c !== card) c.classList.remove('is-touch-flipped');
      });
      card.classList.add('is-touch-flipped');
    });
  });

  document.addEventListener('click', function (e) {
    cards.forEach(function (c) {
      if (!c.contains(e.target)) c.classList.remove('is-touch-flipped');
    });
  });
}

/* ---------------------------------------------------------------------
   Fit Finder: matches the chosen height/weight against merchant-defined
   size rules and reveals the closest recommendation. Picks the rule
   whose range the inputs fall furthest inside (not just the first
   overlapping match), so overlapping ranges resolve sensibly.
   --------------------------------------------------------------------- */
function initFitFinder() {
  var tool = document.querySelector('.fit-finder');
  if (!tool) return;

  var heightSelect = tool.querySelector('[data-fit-height]');
  var weightSelect = tool.querySelector('[data-fit-weight]');
  var submitBtn = tool.querySelector('[data-fit-submit]');
  var result = tool.querySelector('[data-fit-result]');
  var resultSize = tool.querySelector('[data-fit-result-size]');
  var resultNote = tool.querySelector('[data-fit-result-note]');
  var rulesEl = tool.querySelector('[data-fit-rules]');
  if (!heightSelect || !weightSelect || !submitBtn || !rulesEl) return;

  var rules = [];
  try { rules = JSON.parse(rulesEl.textContent); } catch (e) { rules = []; }
  if (!rules.length) return;

  function bestMatch(height, weight) {
    var best = null;
    var bestScore = Infinity;
    rules.forEach(function (rule) {
      var heightMid = (rule.minHeight + rule.maxHeight) / 2;
      var weightMid = (rule.minWeight + rule.maxWeight) / 2;
      var heightSpan = Math.max(1, rule.maxHeight - rule.minHeight);
      var weightSpan = Math.max(1, rule.maxWeight - rule.minWeight);
      var score = Math.abs(height - heightMid) / heightSpan + Math.abs(weight - weightMid) / weightSpan;
      if (score < bestScore) {
        bestScore = score;
        best = rule;
      }
    });
    return best;
  }

  submitBtn.addEventListener('click', function () {
    var height = parseInt(heightSelect.value, 10);
    var weight = parseInt(weightSelect.value, 10);
    var match = bestMatch(height, weight);
    if (!match) return;

    if (resultSize) resultSize.textContent = match.size;
    if (resultNote) resultNote.textContent = match.note || '';
    if (result) result.classList.add('is-visible');
  });
}

/* ---------------------------------------------------------------------
   Quality stats (450GSM+ etc.): counts up from 0 to the target number
   once the stat scrolls into view. Runs once per stat.
   --------------------------------------------------------------------- */
function initQualityStats() {
  var stats = document.querySelectorAll('[data-stat-value]');
  if (!stats.length) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      var countEl = el.querySelector('[data-stat-count]');
      var target = parseInt(el.dataset.statValue, 10) || 0;
      io.unobserve(el);
      if (!countEl) return;

      if (reduceMotion) {
        countEl.textContent = target;
        return;
      }

      var duration = 1200;
      var start = null;
      function step(timestamp) {
        if (!start) start = timestamp;
        var progress = Math.min((timestamp - start) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        countEl.textContent = Math.round(eased * target);
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }, { threshold: 0.4 });

  stats.forEach(function (el) { io.observe(el); });
}

function initHoverSounds() {
  if (!SOUND_ENABLED) return; // no-op until sounds are actually enabled
  document.querySelectorAll('.btn-primary, .btn-ghost, a.collection-card').forEach(function (el) {
    el.addEventListener('mouseenter', function () { playSound('hover'); });
    el.addEventListener('click', function () { playSound('click'); });
  });
}
